import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import ChekinProSettings from 'ChekinProSettings';
import {Button, cn} from '@chekin/guestapp-ui-kit';
import {PATHS} from 'routes/constants';
import {useSummary} from 'context/summary';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useGuestGroup, usePath} from 'hooks';
import {IncompleteRegisteredGuestsStep} from './IncompleteRegisteredGuestsStep';
import {CompletedGuestsStep} from './CompletedGuestsStep';
import {PrintKeyCardButton} from './PrintKeyCardButton';
import {ScanQrAtPropertyInfoBox} from './ScanQrAtPropertyInfoBox';
import {NextStepRegisterContainer, NextStepTitle} from './styled';
import {ShareRegistrationLinkButton} from 'components/ShareRegistrationLinkButton';
import {SDK_MODE} from '@chekin/core';

type Props = {
  closeModal: () => void;
};
function ModalGuestRegisteredNextStepContent({closeModal}: Props) {
  const {areAllGuestsRegistered, guestGroup} = useGuestGroup();
  const {arePaymentsAvailable, areAllPaymentsCompleted} = useSummary();
  const {t} = useTranslation();
  const current = guestGroup?.added_number_of_guests;
  const total = guestGroup?.known_number_of_guests;
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const layoutMode = useLayoutMode();
  const {isIVOnPropertyUnavailable} = useIVOnProperty();

  const showIncompletePaymentsMessage =
    arePaymentsAvailable &&
    !areAllPaymentsCompleted &&
    ChekinProSettings.mode !== SDK_MODE.guestRegistrationOnly &&
    ChekinProSettings.mode !== SDK_MODE.onlyGuestForm;

  const getNextStepTitleKey = React.useMemo(() => {
    if (areAllGuestsRegistered) {
      if (showIncompletePaymentsMessage && layoutMode === 'default') {
        return 'continue_to_booking_payments';
      }
      return 'guest_registration_complete';
    }
    return 'guest_registered';
  }, [areAllGuestsRegistered, showIncompletePaymentsMessage, layoutMode]);

  return (
    <NextStepRegisterContainer className="chekin-next-step-modal__content kiosko:flex-col">
      <NextStepTitle
        className={cn(
          'chekin-next-step-modal__title',
          'kiosko:text-kiosko-l kiosko:mt-0 kiosko:mx-auto kiosko:mb-[78px] kiosko:max-w-full',
        )}
      >
        {t(getNextStepTitleKey)}
      </NextStepTitle>

      <div className={isIVOnPropertyUnavailable ? 'block' : 'hidden'}>
        <ScanQrAtPropertyInfoBox />
      </div>

      {!areAllGuestsRegistered ? (
        <IncompleteRegisteredGuestsStep
          current={current ?? 0}
          total={total ?? 0}
          hideTitle={isIVOnPropertyUnavailable}
        />
      ) : (
        <CompletedGuestsStep
          showIncompletePaymentsMessage={showIncompletePaymentsMessage}
          hideTitle={isIVOnPropertyUnavailable}
        />
      )}

      {ChekinProSettings.mode !== SDK_MODE.onlyGuestForm && (
        <Button
          onClick={() => navigate(getFullPath(PATHS.home))}
          className="w-full kiosko:mx-auto kiosko:mb-12 mt-4"
          variant="outline"
        >
          {t('go_back_to_home')}
        </Button>
      )}

      {!areAllGuestsRegistered && <ShareRegistrationLinkButton className="mt-4" />}

      <PrintKeyCardButton onPrintCard={closeModal} />
    </NextStepRegisterContainer>
  );
}

export {ModalGuestRegisteredNextStepContent};
